import React, { useEffect, useRef, useState } from "react";
import { supportAttachmentUrl } from "./supportService";

const CLIPBOARD_IMAGE_EXTENSIONS = {
  "image/png": ".png",
  "image/jpeg": ".jpg",
  "image/gif": ".gif",
  "image/webp": ".webp",
};
const IMAGE_TOKEN_PATTERN = /\[\[support-image:([^\]]+)\]\]/g;
let nextBlockId = 0;

function blockId(prefix) {
  nextBlockId += 1;
  return `${prefix}-${nextBlockId}`;
}

function clipboardScreenshots(event) {
  const timestamp = new Date()
    .toISOString()
    .replaceAll(":", "-")
    .replaceAll(".", "-");
  const itemFiles = Array.from(event.clipboardData?.items || [])
    .filter(
      (item) =>
        item.kind === "file" && CLIPBOARD_IMAGE_EXTENSIONS[item.type],
    )
    .map((item, index) => {
      const sourceFile = item.getAsFile();
      if (!sourceFile) return null;
      const extension = CLIPBOARD_IMAGE_EXTENSIONS[item.type];
      const suffix = index ? `-${index + 1}` : "";
      return new File(
        [sourceFile],
        `pasted-screenshot-${timestamp}${suffix}${extension}`,
        {
          type: item.type,
          lastModified: Date.now() + index,
        },
      );
    })
    .filter(Boolean);
  if (itemFiles.length) return itemFiles;
  return Array.from(event.clipboardData?.files || [])
    .filter((file) => CLIPBOARD_IMAGE_EXTENSIONS[file.type])
    .map(
      (file, index) =>
        new File(
          [file],
          `pasted-screenshot-${timestamp}${
            index ? `-${index + 1}` : ""
          }${CLIPBOARD_IMAGE_EXTENSIONS[file.type]}`,
          {
            type: file.type,
            lastModified: Date.now() + index,
          },
        ),
    );
}

function imageToken(fileName) {
  return `[[support-image:${encodeURIComponent(fileName)}]]`;
}

function serializeBlocks(blocks) {
  return blocks
    .map((block) =>
      block.type === "image" ? imageToken(block.file.name) : block.text,
    )
    .filter((value) => String(value || "").trim())
    .join("\n\n")
    .trim();
}

function decodedFileName(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function SupportNarrative({ content, attachments = [] }) {
  const source = String(content || "");
  const parts = [];
  let match;
  let startIndex = 0;
  const pattern = new RegExp(IMAGE_TOKEN_PATTERN.source, "g");

  while ((match = pattern.exec(source)) !== null) {
    if (match.index > startIndex) {
      parts.push({
        type: "text",
        value: source.slice(startIndex, match.index),
      });
    }
    parts.push({
      type: "image",
      fileName: decodedFileName(match[1]),
    });
    startIndex = pattern.lastIndex;
  }
  if (startIndex < source.length) {
    parts.push({ type: "text", value: source.slice(startIndex) });
  }
  if (!parts.length && source) {
    parts.push({ type: "text", value: source });
  }

  return (
    <div className="support-narrative">
      {parts.map((part, index) => {
        if (part.type === "text") {
          if (!part.value.trim()) return null;
          return <p key={`text-${index}`}>{part.value.trim()}</p>;
        }
        const attachment = attachments.find(
          (item) => item.file_name === part.fileName,
        );
        if (
          !attachment ||
          !String(attachment.content_type).startsWith("image/")
        ) {
          return (
            <p
              className="support-narrative-missing"
              key={`missing-${index}`}
            >
              Screenshot: {part.fileName}
            </p>
          );
        }
        const url = supportAttachmentUrl(attachment.attachment_id);
        return (
          <figure
            className="support-narrative-image"
            key={attachment.attachment_id}
          >
            <a href={url} target="_blank" rel="noopener noreferrer">
              <img
                src={url}
                alt={`Screenshot ${part.fileName}`}
                loading="lazy"
              />
            </a>
            <figcaption>{part.fileName}</figcaption>
          </figure>
        );
      })}
    </div>
  );
}

function drawAnnotation(context, shape, canvasWidth) {
  const left = Math.min(shape.startX, shape.endX);
  const top = Math.min(shape.startY, shape.endY);
  const width = Math.abs(shape.endX - shape.startX);
  const height = Math.abs(shape.endY - shape.startY);
  const strokeWidth = Math.max(4, canvasWidth * 0.005);

  context.save();
  if (shape.tool === "circle") {
    context.strokeStyle = "#e03131";
    context.lineWidth = strokeWidth;
    context.beginPath();
    context.ellipse(
      left + width / 2,
      top + height / 2,
      Math.max(width / 2, 2),
      Math.max(height / 2, 2),
      0,
      0,
      Math.PI * 2,
    );
    context.stroke();
  } else if (shape.tool === "highlight") {
    context.fillStyle = "rgba(255, 214, 10, 0.34)";
    context.strokeStyle = "rgba(194, 139, 0, 0.78)";
    context.lineWidth = Math.max(2, canvasWidth * 0.0025);
    context.fillRect(left, top, width, height);
    context.strokeRect(left, top, width, height);
  } else if (shape.tool === "rectangle") {
    context.strokeStyle = "#e03131";
    context.lineWidth = strokeWidth;
    context.strokeRect(left, top, width, height);
  } else if (shape.tool === "arrow") {
    const angle = Math.atan2(
      shape.endY - shape.startY,
      shape.endX - shape.startX,
    );
    const headLength = Math.max(14, canvasWidth * 0.018);
    context.strokeStyle = "#e03131";
    context.fillStyle = "#e03131";
    context.lineWidth = strokeWidth;
    context.lineCap = "round";
    context.lineJoin = "round";
    context.beginPath();
    context.moveTo(shape.startX, shape.startY);
    context.lineTo(shape.endX, shape.endY);
    context.stroke();
    context.beginPath();
    context.moveTo(shape.endX, shape.endY);
    context.lineTo(
      shape.endX - headLength * Math.cos(angle - Math.PI / 6),
      shape.endY - headLength * Math.sin(angle - Math.PI / 6),
    );
    context.lineTo(
      shape.endX - headLength * Math.cos(angle + Math.PI / 6),
      shape.endY - headLength * Math.sin(angle + Math.PI / 6),
    );
    context.closePath();
    context.fill();
  } else if (shape.tool === "freehand") {
    const points = shape.points || [];
    if (points.length > 1) {
      context.strokeStyle = "#e03131";
      context.lineWidth = strokeWidth;
      context.lineCap = "round";
      context.lineJoin = "round";
      context.beginPath();
      context.moveTo(points[0].x, points[0].y);
      for (const point of points.slice(1)) {
        context.lineTo(point.x, point.y);
      }
      context.stroke();
    }
  } else if (shape.tool === "text") {
    context.font = `700 ${shape.fontSize}px Arial, sans-serif`;
    context.textBaseline = "top";
    context.lineJoin = "round";
    context.lineWidth = Math.max(3, canvasWidth * 0.004);
    context.strokeStyle = "rgba(255, 255, 255, 0.96)";
    context.fillStyle = "#e03131";
    context.strokeText(shape.text, shape.startX, shape.startY);
    context.fillText(shape.text, shape.startX, shape.startY);
  }
  context.restore();
}

function annotationBounds(context, shape, canvasWidth, includePadding = true) {
  let left;
  let top;
  let right;
  let bottom;

  if (shape.tool === "text") {
    context.save();
    context.font = `700 ${shape.fontSize}px Arial, sans-serif`;
    const textWidth = context.measureText(shape.text).width;
    context.restore();
    left = shape.startX;
    top = shape.startY;
    right = shape.startX + textWidth;
    bottom = shape.startY + shape.fontSize * 1.2;
  } else if (shape.tool === "freehand" && shape.points?.length) {
    const xValues = shape.points.map((point) => point.x);
    const yValues = shape.points.map((point) => point.y);
    left = Math.min(...xValues);
    top = Math.min(...yValues);
    right = Math.max(...xValues);
    bottom = Math.max(...yValues);
  } else {
    left = Math.min(shape.startX, shape.endX);
    top = Math.min(shape.startY, shape.endY);
    right = Math.max(shape.startX, shape.endX);
    bottom = Math.max(shape.startY, shape.endY);
  }

  const padding = includePadding ? Math.max(10, canvasWidth * 0.01) : 0;
  return {
    left: left - padding,
    top: top - padding,
    right: right + padding,
    bottom: bottom + padding,
  };
}

function drawAnnotationSelection(context, shape, canvasWidth) {
  const bounds = annotationBounds(context, shape, canvasWidth);
  context.save();
  context.strokeStyle = "#2563eb";
  context.lineWidth = Math.max(2, canvasWidth * 0.0025);
  context.setLineDash([
    Math.max(7, canvasWidth * 0.007),
    Math.max(5, canvasWidth * 0.005),
  ]);
  context.strokeRect(
    bounds.left,
    bounds.top,
    bounds.right - bounds.left,
    bounds.bottom - bounds.top,
  );
  context.restore();
}

function annotationAtPoint(context, shapes, point, canvasWidth) {
  for (let index = shapes.length - 1; index >= 0; index -= 1) {
    const bounds = annotationBounds(
      context,
      shapes[index],
      canvasWidth,
    );
    if (
      point.x >= bounds.left &&
      point.x <= bounds.right &&
      point.y >= bounds.top &&
      point.y <= bounds.bottom
    ) {
      return index;
    }
  }
  return -1;
}

function translateAnnotation(shape, deltaX, deltaY) {
  return {
    ...shape,
    startX: shape.startX + deltaX,
    startY: shape.startY + deltaY,
    endX: shape.endX + deltaX,
    endY: shape.endY + deltaY,
    ...(shape.points
      ? {
          points: shape.points.map((point) => ({
            x: point.x + deltaX,
            y: point.y + deltaY,
          })),
        }
      : {}),
  };
}

const TOOL_INSTRUCTIONS = {
  move: "Select an annotation, then drag it to a new position.",
  circle: "Drag around an area to add a red circle.",
  highlight: "Drag over an area to add a yellow highlight.",
  rectangle: "Drag around an area to add a red rectangle.",
  arrow: "Drag from the starting point toward the item you want to identify.",
  freehand: "Draw directly on the screenshot with the pointer.",
  text: "Enter text, choose its size, then click the screenshot to place it.",
};

function ScreenshotAnnotationModal({ block, onCancel, onSave }) {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const shapesRef = useRef([]);
  const drawingRef = useRef(null);
  const movingRef = useRef(null);
  const selectedShapeIndexRef = useRef(-1);
  const [tool, setTool] = useState("circle");
  const [annotationText, setAnnotationText] = useState("");
  const [textSize, setTextSize] = useState(32);
  const [shapeCount, setShapeCount] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  function renderCanvas(draftShape = null, showSelection = true) {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    for (const shape of shapesRef.current) {
      drawAnnotation(context, shape, canvas.width);
    }
    if (
      showSelection &&
      selectedShapeIndexRef.current >= 0 &&
      shapesRef.current[selectedShapeIndexRef.current]
    ) {
      drawAnnotationSelection(
        context,
        shapesRef.current[selectedShapeIndexRef.current],
        canvas.width,
      );
    }
    if (draftShape) {
      drawAnnotation(context, draftShape, canvas.width);
    }
  }

  useEffect(() => {
    const image = new Image();
    image.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const scale = Math.min(
        1,
        4096 / Math.max(image.naturalWidth, image.naturalHeight),
      );
      canvas.width = Math.max(
        1,
        Math.round(image.naturalWidth * scale),
      );
      canvas.height = Math.max(
        1,
        Math.round(image.naturalHeight * scale),
      );
      imageRef.current = image;
      setIsReady(true);
      renderCanvas();
    };
    image.src = block.previewUrl;
    // renderCanvas reads refs that are assigned by the image callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.previewUrl]);

  function canvasPoint(event) {
    const canvas = canvasRef.current;
    const bounds = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - bounds.left) * canvas.width) / bounds.width,
      y: ((event.clientY - bounds.top) * canvas.height) / bounds.height,
    };
  }

  function startDrawing(event) {
    if (!isReady) return;
    const point = canvasPoint(event);
    if (tool === "move") {
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      const selectedIndex = annotationAtPoint(
        context,
        shapesRef.current,
        point,
        canvas.width,
      );
      selectedShapeIndexRef.current = selectedIndex;
      movingRef.current =
        selectedIndex >= 0
          ? {
              index: selectedIndex,
              pointerStart: point,
              originalShape: shapesRef.current[selectedIndex],
            }
          : null;
      if (movingRef.current) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      renderCanvas();
      return;
    }
    if (tool === "text") {
      const text = annotationText.trim();
      if (!text) return;
      const shape = {
        tool: "text",
        startX: point.x,
        startY: point.y,
        endX: point.x,
        endY: point.y,
        text,
        fontSize: textSize,
      };
      shapesRef.current = [...shapesRef.current, shape];
      setShapeCount(shapesRef.current.length);
      setAnnotationText("");
      renderCanvas();
      return;
    }
    drawingRef.current = {
      tool,
      startX: point.x,
      startY: point.y,
      endX: point.x,
      endY: point.y,
      ...(tool === "freehand" ? { points: [point] } : {}),
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function continueDrawing(event) {
    if (movingRef.current) {
      const point = canvasPoint(event);
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      const originalBounds = annotationBounds(
        context,
        movingRef.current.originalShape,
        canvas.width,
        false,
      );
      const requestedX = point.x - movingRef.current.pointerStart.x;
      const requestedY = point.y - movingRef.current.pointerStart.y;
      const deltaX = Math.max(
        -originalBounds.left,
        Math.min(canvas.width - originalBounds.right, requestedX),
      );
      const deltaY = Math.max(
        -originalBounds.top,
        Math.min(canvas.height - originalBounds.bottom, requestedY),
      );
      const nextShapes = [...shapesRef.current];
      nextShapes[movingRef.current.index] = translateAnnotation(
        movingRef.current.originalShape,
        deltaX,
        deltaY,
      );
      shapesRef.current = nextShapes;
      renderCanvas();
      return;
    }
    if (!drawingRef.current) return;
    const point = canvasPoint(event);
    drawingRef.current = {
      ...drawingRef.current,
      endX: point.x,
      endY: point.y,
      ...(drawingRef.current.tool === "freehand"
        ? {
            points: [...(drawingRef.current.points || []), point],
          }
        : {}),
    };
    renderCanvas(drawingRef.current);
  }

  function finishDrawing(event) {
    if (movingRef.current) {
      continueDrawing(event);
      movingRef.current = null;
      renderCanvas();
      return;
    }
    if (!drawingRef.current) return;
    const point = canvasPoint(event);
    const shape = {
      ...drawingRef.current,
      endX: point.x,
      endY: point.y,
      ...(drawingRef.current.tool === "freehand"
        ? {
            points: [...(drawingRef.current.points || []), point],
          }
        : {}),
    };
    drawingRef.current = null;
    const horizontalDistance = Math.abs(shape.endX - shape.startX);
    const verticalDistance = Math.abs(shape.endY - shape.startY);
    const straightDistance = Math.hypot(
      shape.endX - shape.startX,
      shape.endY - shape.startY,
    );
    const freehandDistance = (shape.points || [])
      .slice(1)
      .reduce((total, currentPoint, index) => {
        const previousPoint = shape.points[index];
        return (
          total +
          Math.hypot(
            currentPoint.x - previousPoint.x,
            currentPoint.y - previousPoint.y,
          )
        );
      }, 0);
    const isDrawable =
      shape.tool === "freehand"
        ? freehandDistance > 3
        : shape.tool === "arrow"
        ? straightDistance > 5
        : horizontalDistance > 3 && verticalDistance > 3;
    if (isDrawable) {
      shapesRef.current = [...shapesRef.current, shape];
      setShapeCount(shapesRef.current.length);
    }
    renderCanvas();
  }

  function undoShape() {
    shapesRef.current = shapesRef.current.slice(0, -1);
    selectedShapeIndexRef.current = -1;
    movingRef.current = null;
    setShapeCount(shapesRef.current.length);
    renderCanvas();
  }

  function clearShapes() {
    shapesRef.current = [];
    selectedShapeIndexRef.current = -1;
    movingRef.current = null;
    setShapeCount(0);
    renderCanvas();
  }

  function chooseTool(nextTool) {
    selectedShapeIndexRef.current = -1;
    movingRef.current = null;
    setTool(nextTool);
    renderCanvas();
  }

  function saveScreenshot() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setIsSaving(true);
    renderCanvas(null, false);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setIsSaving(false);
          renderCanvas();
          return;
        }
        const baseName = block.file.name.replace(/\.[^.]+$/, "");
        const fileName = baseName.endsWith("-annotated")
          ? `${baseName}.png`
          : `${baseName}-annotated.png`;
        const annotatedFile = new File([blob], fileName, {
          type: "image/png",
          lastModified: Date.now(),
        });
        onSave(annotatedFile);
      },
      "image/png",
      0.95,
    );
  }

  return (
    <div className="support-annotation-backdrop" role="presentation">
      <section
        className="support-annotation-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-annotation-title"
      >
        <div className="support-annotation-heading">
          <div>
            <p className="support-card-kicker">Screenshot Editor</p>
            <h3 id="support-annotation-title">Mark up this screenshot</h3>
          </div>
          <button
            className="support-button support-button-secondary btn btn-outline-secondary"
            type="button"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
        <div
          className="support-annotation-toolbar"
          aria-label="Annotation tools"
        >
          {[
            ["move", "Move Annotations"],
            ["circle", "Draw Circle"],
            ["highlight", "Highlight Area"],
            ["rectangle", "Draw Rectangle"],
            ["arrow", "Draw Arrow"],
            ["freehand", "Draw Freehand"],
            ["text", "Add Text"],
          ].map(([value, label]) => (
            <button
              key={value}
              className={`support-button support-button-secondary btn btn-outline-secondary${
                tool === value ? " is-selected" : ""
              }`}
              type="button"
              aria-pressed={tool === value}
              disabled={value === "move" && !shapeCount}
              onClick={() => chooseTool(value)}
            >
              {label}
            </button>
          ))}
          <button
            className="support-button support-button-secondary btn btn-outline-secondary"
            type="button"
            disabled={!shapeCount}
            onClick={undoShape}
          >
            Undo
          </button>
          <button
            className="support-button support-button-secondary btn btn-outline-secondary"
            type="button"
            disabled={!shapeCount}
            onClick={clearShapes}
          >
            Clear
          </button>
        </div>
        {tool === "text" ? (
          <div className="support-annotation-text-controls">
            <label>
              <span>Text to add</span>
              <input
                className="support-text-input form-control"
                type="text"
                value={annotationText}
                maxLength={120}
                placeholder="Type the annotation"
                onChange={(event) =>
                  setAnnotationText(event.target.value)
                }
              />
            </label>
            <div
              className="support-annotation-text-size"
              aria-label="Text size"
            >
              <button
                className="support-button support-button-secondary btn btn-outline-secondary"
                type="button"
                aria-label="Make text smaller"
                disabled={textSize <= 14}
                onClick={() =>
                  setTextSize((value) => Math.max(14, value - 6))
                }
              >
                A−
              </button>
              <output aria-live="polite">{textSize} px</output>
              <button
                className="support-button support-button-secondary btn btn-outline-secondary"
                type="button"
                aria-label="Make text larger"
                disabled={textSize >= 96}
                onClick={() =>
                  setTextSize((value) => Math.min(96, value + 6))
                }
              >
                A+
              </button>
            </div>
          </div>
        ) : null}
        <div className="support-annotation-canvas-shell">
          {!isReady ? <p>Loading screenshot…</p> : null}
          <canvas
            ref={canvasRef}
            className={`support-annotation-canvas${
              tool === "move" ? " is-move-tool" : ""
            }`}
            aria-label="Screenshot annotation canvas"
            onPointerDown={startDrawing}
            onPointerMove={continueDrawing}
            onPointerUp={finishDrawing}
            onPointerCancel={finishDrawing}
          />
        </div>
        <div className="support-annotation-footer">
          <span>{TOOL_INSTRUCTIONS[tool]}</span>
          <button
            className="support-button support-button-primary btn btn-primary"
            type="button"
            disabled={!isReady || isSaving}
            onClick={saveScreenshot}
          >
            {isSaving ? "Saving" : "Save Screenshot"}
          </button>
        </div>
      </section>
    </div>
  );
}

export default function SupportNarrativeEditor({
  id,
  label,
  value = "",
  compact = false,
  onChange,
  onAddScreenshots,
  onRemoveScreenshot,
  onReplaceScreenshot,
}) {
  const previewUrlsRef = useRef(new Set());
  const pendingFocusRef = useRef("");
  const [blocks, setBlocks] = useState([
    { id: blockId("text"), type: "text", text: value },
  ]);
  const [editingBlockId, setEditingBlockId] = useState("");

  useEffect(
    () => () => {
      for (const previewUrl of previewUrlsRef.current) {
        URL.revokeObjectURL(previewUrl);
      }
    },
    [],
  );

  useEffect(() => {
    if (!pendingFocusRef.current) return;
    document.getElementById(pendingFocusRef.current)?.focus();
    pendingFocusRef.current = "";
  }, [blocks]);

  function commit(nextBlocks) {
    setBlocks(nextBlocks);
    onChange(serializeBlocks(nextBlocks));
  }

  function updateText(blockIndex, text) {
    const nextBlocks = blocks.map((block, index) =>
      index === blockIndex ? { ...block, text } : block,
    );
    commit(nextBlocks);
  }

  function pasteIntoBlock(event, blockIndex) {
    const screenshots = clipboardScreenshots(event);
    if (!screenshots.length) return;
    event.preventDefault();
    const imageBlocks = screenshots.map((file) => {
      const previewUrl = URL.createObjectURL(file);
      previewUrlsRef.current.add(previewUrl);
      return {
        id: blockId("image"),
        type: "image",
        file,
        previewUrl,
      };
    });
    const trailingTextBlock = {
      id: blockId("text"),
      type: "text",
      text: "",
    };
    const nextBlocks = [
      ...blocks.slice(0, blockIndex + 1),
      ...imageBlocks,
      trailingTextBlock,
      ...blocks.slice(blockIndex + 1),
    ];
    pendingFocusRef.current = `${id}-${trailingTextBlock.id}`;
    onAddScreenshots(screenshots);
    commit(nextBlocks);
  }

  function removeImage(block) {
    URL.revokeObjectURL(block.previewUrl);
    previewUrlsRef.current.delete(block.previewUrl);
    onRemoveScreenshot(block.file);
    commit(blocks.filter((item) => item.id !== block.id));
  }

  function replaceImage(block, annotatedFile) {
    const previewUrl = URL.createObjectURL(annotatedFile);
    previewUrlsRef.current.add(previewUrl);
    URL.revokeObjectURL(block.previewUrl);
    previewUrlsRef.current.delete(block.previewUrl);
    onReplaceScreenshot(block.file, annotatedFile);
    commit(
      blocks.map((item) =>
        item.id === block.id
          ? { ...item, file: annotatedFile, previewUrl }
          : item,
      ),
    );
    setEditingBlockId("");
  }

  const editingBlock = blocks.find(
    (block) => block.id === editingBlockId,
  );
  let textBlockNumber = 0;
  let imageBlockNumber = 0;

  return (
    <React.Fragment>
      <div
        className={`support-inline-editor${compact ? " is-compact" : ""}`}
      >
        {blocks.map((block, blockIndex) => {
          if (block.type === "text") {
            textBlockNumber += 1;
            const currentTextNumber = textBlockNumber;
            const textId =
              blockIndex === 0 ? id : `${id}-${block.id}`;
            return (
              <textarea
                id={textId}
                key={block.id}
                className="support-inline-text"
                aria-label={
                  blockIndex === 0
                    ? label
                    : `Additional ${label.toLowerCase()} text ${
                        currentTextNumber - 1
                      }`
                }
                maxLength={label === "Description" ? 20000 : 10000}
                value={block.text}
                onChange={(event) =>
                  updateText(blockIndex, event.target.value)
                }
                onPaste={(event) => pasteIntoBlock(event, blockIndex)}
              />
            );
          }
          imageBlockNumber += 1;
          return (
            <figure className="support-inline-image" key={block.id}>
              <img
                src={block.previewUrl}
                alt={`Pasted screenshot ${imageBlockNumber}`}
              />
              <figcaption>
                <span>
                  <strong>{block.file.name}</strong>
                  <small>Inserted in the {label.toLowerCase()}</small>
                </span>
                <span className="support-inline-image-actions">
                  <button
                    className="support-button support-button-secondary btn btn-outline-secondary"
                    type="button"
                    onClick={() => setEditingBlockId(block.id)}
                  >
                    Edit Screenshot
                  </button>
                  <button
                    className="support-staged-remove"
                    type="button"
                    onClick={() => removeImage(block)}
                  >
                    Remove
                  </button>
                </span>
              </figcaption>
            </figure>
          );
        })}
        <p className="support-inline-hint">
          Type normally. Paste a screenshot wherever it belongs, then
          continue typing below it.
        </p>
      </div>
      {editingBlock ? (
        <ScreenshotAnnotationModal
          block={editingBlock}
          onCancel={() => setEditingBlockId("")}
          onSave={(annotatedFile) =>
            replaceImage(editingBlock, annotatedFile)
          }
        />
      ) : null}
    </React.Fragment>
  );
}
